import React, { useEffect, useState } from "react";
import { Box, Drawer, Button, IconButton, Slider, Switch, TextField } from "@mui/material";
import { useDispatch } from "react-redux";
import { IconX, IconTrash } from "@tabler/icons-react";

import { createJobAPI, updateJobAPI, deleteJobAPI } from "../../../services/jobService";
import { showAppAlert } from "../../../store/reducers/appreducer";
import InterviewConfigForm from "./InterviewConfigForm";
import tokens from "../../theme/tokens";

const { color, font, layout, radius, shadow } = tokens;

/** The 5 weighting_parameters the scoring API accepts. */
const WEIGHTS = [
  ["relevance", "Relevance"],
  ["skills", "Skills"],
  ["education", "Education"],
  ["work_experience", "Experience"],
  ["achievements", "Achievements"],
];

const EVEN = Object.fromEntries(WEIGHTS.map(([k]) => [k, 20]));

const Label = ({ children }) => (
  <Box sx={{ mb: 0.75, font: `500 12px ${font.sans}`, color: color.textSecondary }}>
    {children}
  </Box>
);

const field = {
  "& .MuiOutlinedInput-root": { borderRadius: `${radius.control}px` },
  "& .MuiOutlinedInput-input": { padding: "12px 13px", fontSize: 13.5 },
};

/**
 * Create/edit a job in a right-side panel over the console — replaces the full-page
 * form swap. Weights are entered as percentages (they must visibly total 100), then
 * normalised to fractions on submit, which is what the API expects.
 */
const JobPanel = ({ open, onClose, job, onSaved, onDeleted }) => {
  const dispatch = useDispatch();
  const isEdit = Boolean(job);

  const [form, setForm] = useState({ title: "", company: "", location: "", description: "" });
  const [weights, setWeights] = useState(EVEN);
  const [dualModel, setDualModel] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [tab, setTab] = useState("details");

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setTab("details");
    if (job) {
      setForm({
        title: job["Job Title"] || "",
        company: job["Company Name"] || "",
        location: job.Location || "",
        description: job.description || "",
      });
      // Stored as fractions; show them as percentages.
      const wp = job.weighting_parameters;
      setWeights(
        wp
          ? Object.fromEntries(WEIGHTS.map(([k]) => [k, Math.round((wp[k] ?? 0.2) * 100)]))
          : EVEN
      );
      setDualModel(Boolean(job.dual_model_scoring));
    } else {
      setForm({ title: "", company: "", location: "", description: "" });
      setWeights(EVEN);
      setDualModel(false);
    }
  }, [open, job]);

  const total = WEIGHTS.reduce((sum, [k]) => sum + weights[k], 0);
  const valid = total === 100;

  const handleSave = async () => {
    const next = {};
    ["title", "company", "location", "description"].forEach((k) => {
      if (!form[k]?.trim()) next[k] = "This field is required";
    });
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }
    if (!valid) return;

    // API takes fractions summing to 1.
    const weighting_parameters = Object.fromEntries(
      WEIGHTS.map(([k]) => [k, weights[k] / 100])
    );

    // Create and update hit the same schema, so they take the same field names.
    // These are the API's names, not the form's — see jobService for why that matters.
    const payload = {
      job_title: form.title,
      company_name: form.company,
      location: form.location,
      description: form.description,
      weighting_parameters,
      dual_model_scoring: dualModel,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await updateJobAPI(job["Job Id"], payload);
      } else {
        await createJobAPI(payload);
      }
      dispatch(
        showAppAlert({
          show: true,
          alertType: "success",
          alertMessage: isEdit ? "Job updated." : "Job created.",
        })
      );
      onSaved?.();
      onClose();
    } catch (e) {
      dispatch(
        showAppAlert({ show: true, alertType: "error", alertMessage: e.message })
      );
    } finally {
      setSaving(false);
    }
  };

  // Destructive and irreversible — it takes the job's candidates and scores with it,
  // so make the user type nothing but at least confirm against the job's actual name.
  const handleDelete = async () => {
    const ok = window.confirm(
      `Delete "${job["Job Title"]}"?\n\nThis removes the job along with its candidates, scores and screening history. This cannot be undone.`
    );
    if (!ok) return;

    setDeleting(true);
    try {
      await deleteJobAPI(job["Job Id"]);
      dispatch(
        showAppAlert({ show: true, alertType: "success", alertMessage: "Job deleted." })
      );
      onDeleted?.();
      onClose();
    } catch (e) {
      dispatch(showAppAlert({ show: true, alertType: "error", alertMessage: e.message }));
    } finally {
      setDeleting(false);
    }
  };

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((x) => ({ ...x, [k]: "" }));
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: layout.createPanel,
          maxWidth: "100%",
          boxShadow: shadow.panel,
          borderLeft: `1px solid ${color.border}`,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", p: 2.75, pb: 2 }}>
        <Box sx={{ flex: 1, font: `600 15px ${font.sans}`, color: color.text }}>
          {isEdit ? "Job settings" : "New job"}
        </Box>
        <IconButton size="small" onClick={onClose}>
          <IconX size={16} stroke={1.75} color={color.textMuted} />
        </IconButton>
      </Box>

      {/* Interview config used to be its own route. It's a property of the job, so it
          lives here — but only once the job exists to attach it to. */}
      {isEdit && (
        <Box sx={{ display: "flex", gap: 2.5, px: 2.75, borderBottom: `1px solid ${color.border}` }}>
          {[
            ["details", "Details & scoring"],
            ["interview", "AI interview"],
          ].map(([key, label]) => (
            <Box
              key={key}
              onClick={() => setTab(key)}
              sx={{
                py: 1.25,
                cursor: "pointer",
                font: `${tab === key ? 600 : 500} 12.5px ${font.sans}`,
                color: tab === key ? color.indigo : color.textSecondary,
                borderBottom: `2px solid ${tab === key ? color.indigo : "transparent"}`,
                mb: "-1px",
              }}
            >
              {label}
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ flex: 1, overflowY: "auto", px: 2.75, py: 2.5 }}>
        {isEdit && tab === "interview" ? (
          <InterviewConfigForm jobId={job["Job Id"]} onClose={onClose} />
        ) : (
        <>
        <Label>Job title</Label>
        <TextField
          fullWidth
          size="small"
          value={form.title}
          onChange={set("title")}
          error={Boolean(errors.title)}
          helperText={errors.title}
          sx={{ ...field, mb: 2 }}
        />

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 2 }}>
          <Box>
            <Label>Company</Label>
            <TextField
              fullWidth
              size="small"
              value={form.company}
              onChange={set("company")}
              error={Boolean(errors.company)}
              helperText={errors.company}
              sx={field}
            />
          </Box>
          <Box>
            <Label>Location</Label>
            <TextField
              fullWidth
              size="small"
              value={form.location}
              onChange={set("location")}
              error={Boolean(errors.location)}
              helperText={errors.location}
              sx={field}
            />
          </Box>
        </Box>

        <Label>Description</Label>
        <TextField
          fullWidth
          multiline
          minRows={4}
          value={form.description}
          onChange={set("description")}
          error={Boolean(errors.description)}
          helperText={errors.description}
          sx={{ ...field, mb: 3 }}
        />

        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
          <Box sx={{ flex: 1, font: `500 12px ${font.sans}`, color: color.textSecondary }}>
            Scoring weights
          </Box>
          <Box
            sx={{
              font: `400 11px ${font.mono}`,
              color: valid ? color.success : color.danger,
            }}
          >
            total {total}% {valid ? "✓" : ""}
          </Box>
        </Box>

        {WEIGHTS.map(([key, label]) => (
          <Box
            key={key}
            sx={{
              display: "grid",
              gridTemplateColumns: "110px 1fr 44px",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box sx={{ font: `400 12px ${font.sans}`, color: color.text }}>{label}</Box>
            <Slider
              value={weights[key]}
              onChange={(_, v) => setWeights((w) => ({ ...w, [key]: v }))}
              min={0}
              max={100}
              step={5}
              sx={{
                py: 1.5,
                color: color.indigo,
                "& .MuiSlider-rail": { height: 5, bgcolor: color.borderRow, opacity: 1 },
                "& .MuiSlider-track": { height: 5, border: "none" },
                "& .MuiSlider-thumb": {
                  width: 13,
                  height: 13,
                  bgcolor: "#fff",
                  border: `2px solid ${color.indigo}`,
                },
              }}
            />
            <Box sx={{ textAlign: "right", font: `500 12px ${font.mono}`, color: color.textSecondary }}>
              {weights[key]}%
            </Box>
          </Box>
        ))}

        <Box
          sx={{
            mt: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            border: `1px solid ${color.border}`,
            borderRadius: `${radius.fileRow}px`,
            bgcolor: color.bg,
            px: 1.75,
            py: 1.5,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ font: `500 12.5px ${font.sans}`, color: color.text }}>
              Dual-model scoring
            </Box>
            <Box sx={{ mt: 0.25, font: `400 11px ${font.sans}`, color: color.textMuted }}>
              Score each resume with two models and reconcile. Slower, more accurate.
            </Box>
          </Box>
          <Switch
            checked={dualModel}
            onChange={(e) => setDualModel(e.target.checked)}
            size="small"
          />
        </Box>
        </>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2.75,
          py: 2,
          borderTop: `1px solid ${color.border}`,
          bgcolor: color.bg,
        }}
      >
        {isEdit && (
          <Button
            onClick={handleDelete}
            disabled={deleting}
            startIcon={<IconTrash size={15} stroke={1.75} />}
            sx={{ color: color.danger, fontWeight: 500 }}
          >
            {deleting ? "Deleting…" : "Delete job"}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" onClick={onClose}>
          {tab === "interview" ? "Close" : "Cancel"}
        </Button>
        {tab !== "interview" && (
          <Button variant="contained" onClick={handleSave} disabled={saving || !valid}>
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create job"}
          </Button>
        )}
      </Box>
    </Drawer>
  );
};

export default JobPanel;
